import { useCallback, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { isTouchLike } from "../lib/device";

/**
 * Click-and-drag panning для горизонтального скролл-контейнера (Trello/Miro).
 * Возвращает пропсы-обёртку — навешиваются прямо на div, без useRef/addEventListener.
 * mousemove/mouseup регистрируются на document внутри mousedown, чтобы
 * корректно отслеживать движение даже когда курсор уходит за пределы элемента.
 */
const IGNORE_SELECTOR =
  '.card, button, a, input, textarea, select, label, [draggable="true"], .col-menu, [contenteditable="true"]';

export function usePanScroll() {
  const [panning, setPanning] = useState(false);
  const elRef = useRef<HTMLDivElement | null>(null);

  const onMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if (isTouchLike()) return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest(IGNORE_SELECTOR)) return;

    const el = e.currentTarget;
    elRef.current = el;
    const startX = e.clientX;
    const startY = e.clientY;
    const startScrollLeft = el.scrollLeft;
    const startScrollY = window.scrollY;
    let moved = false;

    e.preventDefault();

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!moved) {
        if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
        moved = true;
        setPanning(true);
        document.body.style.userSelect = "none";
      }
      ev.preventDefault();
      el.scrollLeft = startScrollLeft - dx;
      window.scrollTo(0, startScrollY - dy);
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("dragstart", onDragBlock, true);
      document.body.style.userSelect = "";
      if (moved) setPanning(false);
    };

    // Глушим native HTML5 DnD, если курсор прошёл над карточкой.
    const onDragBlock = (ev: DragEvent) => ev.preventDefault();

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("dragstart", onDragBlock, true);
  }, []);

  return { onMouseDown, panning };
}
